// Resilient WebSocket helper with auto-reconnect, heartbeat, and JSON events.
export class WSClient {
  // Static map to track active connections and prevent duplicates
  static activeConnections = new Map();

  // Close all active WS connections (used when stopping a sales call)
  static closeAll() {
    try {
      for (const [url, client] of WSClient.activeConnections.entries()) {
        try {
          console.log(`[WS] 🛑 Closing active connection: ${url}`);
          client.close();
        } catch {
        }
      }
    } catch {
    }
  }

  /**
   * @param {string} url - full ws(s):// URL
   * @param {{onMessage?:(data:any)=>void,onOpen?:()=>void,onClose?:(ev:CloseEvent)=>void,onError?:(ev:Event)=>void, protocols?:string|string[]}} [opts]
   */
  constructor(url, opts = {}) {
    // Check if connection already exists for this URL
    if (WSClient.activeConnections.has(url)) {
      const existingConnection = WSClient.activeConnections.get(url);
      console.log(`[WS] Reusing existing connection for: ${url}`);
      return existingConnection;
    }

    this.url = url;
    this.protocols = opts.protocols;
    this.onMessage = opts.onMessage || (() => {});
    this.onOpen = opts.onOpen || (() => {});
    this.onClose = opts.onClose || (() => {});
    this.onError = opts.onError || (() => {});
    this.ws = null;

    // reconnect
    this._reconnectAttempts = 0;
    this._closedByUser = false;

    // heartbeat
    this._pingTimer = null;
    this._PING_MS = 30000; // Increased to 30 seconds to be more conservative

    // Store this connection
    WSClient.activeConnections.set(url, this);

    this._connect();
  }

  _connect() {
    console.log(`[WS] Connecting to: ${this.url}`);
    this.ws = new WebSocket(this.url, this.protocols);
    this.ws.onopen = () => {
      const isSummarizer = this.url.includes('/summarizer');
      if (isSummarizer) {
        console.log('[WS_CLIENT] ========== SUMMARIZER WEBSOCKET CONNECTED ==========');
        console.log('[WS_CLIENT] URL:', this.url);
      }
      console.log(`[WS] ✅ Connected to: ${this.url}`);
      this._reconnectAttempts = 0;
      this._startHeartbeat();
      this.onOpen();
      if (isSummarizer) {
        console.log('[WS_CLIENT] ============================================');
      }
    };
    this.ws.onmessage = (evt) => {
      const raw = evt.data;
      
      // Special logging for summarizer WebSocket
      const isSummarizer = this.url.includes('/summarizer');
      
      if (isSummarizer) {
        console.log('[WS_CLIENT] ========== SUMMARIZER MESSAGE RECEIVED ==========');
        console.log('[WS_CLIENT] URL:', this.url);
        console.log('[WS_CLIENT] Raw data length:', raw.length);
        console.log('[WS_CLIENT] Raw data:', raw);
      }
      
      try {
        const parsed = JSON.parse(raw);
        
        if (isSummarizer) {
          console.log('[WS_CLIENT] ✅ SUMMARIZER: Successfully parsed JSON');
          console.log('[WS_CLIENT] Parsed data:', {
            type: parsed.type,
            hasSummary: !!parsed.summary,
            summaryLength: parsed.summary?.length || 0,
            keyPoints: parsed.keyPoints,
            actionItems: parsed.actionItems,
            nextStep: parsed.nextStep,
            fullData: parsed
          });
        }
        
        // Only log transcription data in development mode to reduce overhead
        if (process.env.NODE_ENV === 'development' && parsed && (parsed.type === "result" || parsed.Type === "result") && parsed.text) {
          console.log(`[CLIENT] Received transcription:`, {
            deviceId: parsed.deviceId || parsed.DeviceId || 'unknown',
            index: parsed.index || parsed.Index,
            text: parsed.text || parsed.Text,
            timestamp: parsed.ts || parsed.Timestamp || Date.now()
          });
        }
        
        if (isSummarizer) {
          console.log('[WS_CLIENT] ✅✅✅ SUMMARIZER: Calling onMessage with parsed data');
        }
        this.onMessage(parsed);
        if (isSummarizer) {
          console.log('[WS_CLIENT] ================================================');
        }
      } catch (err) {
        if (isSummarizer) {
          console.error('[WS_CLIENT] ❌❌❌ SUMMARIZER: Failed to parse JSON:', err);
          console.error('[WS_CLIENT] Raw data that failed:', raw);
        }
        // emit raw if not JSON
        this.onMessage(raw);
      }
    };
    this.ws.onerror = (ev) => {
      console.error(`[WS] ❌ Connection error:`, {
        type: ev.type,
        url: this.url,
        readyState: this.ws?.readyState,
        timestamp: new Date().toISOString(),
        error: ev,
        userAgent: navigator.userAgent,
        protocol: window.location.protocol
      });
      this.onError(ev);
    };
    this.ws.onclose = (ev) => {
      const closeInfo = {
        code: ev.code,
        reason: ev.reason,
        wasClean: ev.wasClean,
        url: this.url,
        timestamp: new Date().toISOString(),
        reconnectAttempts: this._reconnectAttempts,
        closedByUser: this._closedByUser
      };

      // Detailed logging for specific error codes
      if (ev.code === 1006) {
        console.error(`[WS] 🔌 ABNORMAL CLOSURE (1006):`, {
          ...closeInfo,
          possibleCauses: [
            'Server crashed or stopped unexpectedly',
            'Network connectivity issues',
            'SSL/TLS handshake failure',
            'Firewall blocking connection',
            'Server overloaded or timeout',
            'Invalid WebSocket URL or protocol mismatch'
          ],
          troubleshooting: [
            'Check if server is running on correct port',
            'Verify HTTPS/WSS protocol matches server configuration',
            'Check browser console for SSL certificate errors',
            'Test server connectivity with curl: curl -k https://localhost:8080/set-up/is-up',
            'Check server logs for errors'
          ]
        });
      } else {
        console.log(`[WS] 🔌 Connection closed:`, closeInfo);
      }

      this._stopHeartbeat();
      this.onClose(ev);
      if (!this._closedByUser) {
        const delay = Math.min(1000 * 2 ** this._reconnectAttempts, 15000);
        this._reconnectAttempts += 1;
        console.log(`[WS] 🔄 Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts})`);
        setTimeout(() => this._connect(), delay);
      }
    };
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this._pingTimer = setInterval(() => {
      try {
        this.ws?.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      } catch {
        // Ignore close errors
      }
    }, this._PING_MS);
  }

  _stopHeartbeat() {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }

  send(obj) {
    const msg = typeof obj === "string" ? obj : JSON.stringify(obj);
    this.ws?.send(msg);
  }

  close() {
    console.log(`[WS] 🛑 Closing connection`);
    this._closedByUser = true;
    this._stopHeartbeat();
    
    // Remove from active connections
    WSClient.activeConnections.delete(this.url);
    
    try {
      this.ws?.close();
    } catch {
      // Ignore close errors
    }
  }
}
